/**
 * Global keyboard listener.
 *
 * Installs a single `window` keydown listener (one subscription, mounted once by
 * `KeyboardHost`) that dispatches the `global` context bindings. Reads the frozen
 * registry synchronously — no React state, no re-render. Printable chords without
 * a `mod` (e.g. `?`) are ignored while an editable element is focused so typing
 * is never hijacked; handlers report whether they consumed the event so
 * unhandled keys (e.g. Esc with no overlay open) fall through to local handlers.
 */

import { useEffect } from 'react';
import { pendingSavesActions } from '@/store/pendingSavesStore';
import { keybindingRegistry } from './keybindingRegistry';
import { matchChord } from './matchChord';
import { keyboardOverlayActions, keyboardOverlayState } from './keyboardOverlayStore';
import { searchTarget } from './searchTarget';

type GlobalHandler = (e: KeyboardEvent) => boolean;

const globalHandlers: Record<string, GlobalHandler> = {
  'global.search': () => searchTarget.focus(),
  'global.save': () => {
    pendingSavesActions.flushAll();
    return true;
  },
  'global.cheatSheet': () => {
    keyboardOverlayActions.toggleCheatSheet();
    return true;
  },
  'global.commandPalette': () => {
    keyboardOverlayActions.togglePalette();
    return true;
  },
  'global.escape': () => {
    if (keyboardOverlayState.paletteOpen || keyboardOverlayState.cheatSheetOpen) {
      keyboardOverlayActions.closeAll();
      return true;
    }
    return false;
  },
};

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

export function useGlobalKeybindings(): void {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      const editable = isEditableTarget(e.target);
      for (const binding of keybindingRegistry.byContext('global')) {
        const handler = globalHandlers[binding.id];
        if (!handler) continue;
        const chord = binding.chords.find((c) => matchChord(e, c));
        if (!chord) continue;
        if (editable && !chord.mod && binding.id !== 'global.escape') continue;
        if (handler(e)) {
          e.preventDefault();
          return;
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
